import {
  useGetBatches,
  getGetBatchesQueryOptions,
} from "@/requests/gen/react-query/batch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { ColumnDef } from "@tanstack/react-table";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { format, parseISO } from "date-fns";
import {
  createLoader,
  parseAsBoolean,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import {
  createFileRoute,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Toggle } from "@/components/ui/toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CircleHelp } from "lucide-react";

const batchSearchParsers = {
  has_arrived: parseAsBoolean,
};

const batchSearchAllParsers = {
  ...dataTableSearchParsers,
  ...batchSearchParsers,
};

const loadBatchSearch = createLoader(batchSearchAllParsers);

type BatchSearch = inferParserType<typeof batchSearchAllParsers>;

function batchQueryParams(
  p: BatchSearch,
): Parameters<typeof getGetBatchesQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    has_arrived: p.has_arrived,
  };
}

export const Route = createFileRoute("/_private/_warehouse-360/batch/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetBatchesQueryOptions(
        batchQueryParams(loadBatchSearch(location.searchStr)),
      ),
    ),
  component: BatchLookupPage,
});

// Define the columns for the table
interface BatchTableProps {
  link?: LinkProps;
  batch_id: string;
  name: string;
  ordered_at?: string | null;
  arrived_at?: string | null;
  vendor?: string;
  owner: string;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<BatchTableProps>[] = [
  { header: "Batch ID", accessorKey: "batch_id" },
  { header: "Name", accessorKey: "name" },
  { header: "Vendor", accessorKey: "vendor" },
  {
    header: "Owner",
    accessorKey: "owner",
  },
  {
    header: "Ordered At",
    accessorKey: "ordered_at",
    enableSorting: true,
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
  },
  {
    header: "Arrived At",
    accessorKey: "arrived_at",
    enableSorting: true,
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
  },
];

// Main component
function BatchLookupPage() {
  const [queryStates, setQueryStates] = useQueryStates(batchSearchAllParsers);

  // Get the batches
  const batches = useGetBatches(batchQueryParams(queryStates), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  // Map the data to the table format
  const tableData: BatchTableProps[] | undefined = batches.data?.items.map(
    (batch) => {
      return {
        link: linkOptions({
          to: "/batch/$batchId",
          params: { batchId: String(batch.batch_id) },
        }),
        batch_id: batch.external_batch_id || batch.batch_id.toString(),
        name: batch.warehouse_vendor.warehouse.catalog_product.display_name,
        vendor: batch.warehouse_vendor.vendor?.name,
        ordered_at: batch.ordered_at,
        arrived_at: batch.arrived_at,
        owner: batch.owner.name,
      };
    },
  );

  // Function to apply or clear the "Has-Arrived" preset
  const toggleHasArrived = () => {
    if (isHasArrivedActive) {
      // Clear filters that define the preset
      setQueryStates({
        has_arrived: null,
      });
    } else {
      // Apply filters that define the preset
      setQueryStates({
        has_arrived: true,
      });
    }
  };

  // Check if the current filters match the "Has-Arrived" preset
  const isHasArrivedActive = queryStates.has_arrived === true;

  // Function to apply or clear the "Has-Not-Arrived" preset
  const toggleHasNotArrived = () => {
    if (isHasNotArrivedActive) {
      // Clear filters that define the preset
      setQueryStates({
        has_arrived: null,
      });
    } else {
      // Apply filters that define the preset
      setQueryStates({
        has_arrived: false,
      });
    }
  };

  // Check if the current filters match the "Has-Not-Arrived" preset
  const isHasNotArrivedActive = queryStates.has_arrived === false;

  return (
    <div className="text-left">
      <title>Batches - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Batches", link: { to: "/batch/lookup" } }]} />
      <Card>
        <CardHeader>
          <CardTitle>Batches</CardTitle>
          <CardDescription>
            View batches below. You can search, filter, and click to see more
            details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={batches.isFetching} />
            {/* Preset */}
            <Toggle
              pressed={isHasArrivedActive}
              onPressedChange={toggleHasArrived}
              size="default"
              className="ml-2 flex items-center gap-1"
            >
              Has Arrived
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1">
                    <CircleHelp className="text-muted-foreground size-4 cursor-pointer" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Batches with an Arrived At date.
                </TooltipContent>
              </Tooltip>
            </Toggle>
            <Toggle
              pressed={isHasNotArrivedActive}
              onPressedChange={toggleHasNotArrived}
              size="default"
              className="ml-2 flex items-center gap-1"
            >
              Has Not Arrived
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1">
                    <CircleHelp className="text-muted-foreground size-4 cursor-pointer" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Batches with No Arrived At date.
                </TooltipContent>
              </Tooltip>
            </Toggle>
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={batches.isLoading}
            isError={batches.isError}
            count={batches.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
